import { Link } from "react-router-dom";
import { Linkedin, Instagram, Youtube, Facebook, MapPin, Phone } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { SITE, SOLUTIONS } from "@/data/content";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M18.9 2H22l-7.6 8.7L23 22h-6.8l-5.3-6.9L4.8 22H1.7l8.1-9.3L0 2h7l4.8 6.3L18.9 2zm-1.2 18h1.9L7.1 3.9H5.1L17.7 20z" /></svg>
);

const cols = [
  { title: "Solutions", links: SOLUTIONS.map((s) => ({ name: s.title, to: `/solutions/${s.slug}` })) },
  { title: "Company", links: [
    { name: "Industries", to: "/industries" }, { name: "Technology", to: "/technology" },
    { name: "About", to: "/about" }, { name: "Insights", to: "/insights" }, { name: "Contact", to: "/contact" },
  ] },
];

const socials = [
  { name: "LinkedIn", Icon: Linkedin, href: "https://www.linkedin.com/company/10973128" }, { name: "X", Icon: XIcon }, { name: "Instagram", Icon: Instagram },
  { name: "YouTube", Icon: Youtube }, { name: "Facebook", Icon: Facebook },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t border-line bg-navy-900">
    <div className="wrap grid gap-14 py-20 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Logo className="h-16 w-auto" />
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-400">
          {SITE.tagline}. Designing, integrating and deploying connected transportation solutions from {SITE.location}.
        </p>
        <address className="mt-6 space-y-2 text-sm not-italic text-slate-400">
          <div data-testid="footer-address" className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /><span>{SITE.address}</span></div>
          {SITE.phones.map((p) => (
            <div key={p.tel} className="flex gap-2"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /><a href={`tel:${p.tel}`} data-testid={`footer-phone-${p.tel}`} className="transition-colors hover:text-cyan-400">{p.label}</a></div>
          ))}
        </address>
        <div className="mt-8 flex items-center gap-2">
          {socials.map(({ name, Icon, href }) => (
            <a key={name} href={href || "#"} target={href ? "_blank" : undefined} rel={href ? "noopener noreferrer" : undefined} aria-label={name} data-testid={`footer-social-${name.toLowerCase()}`}
              onClick={href ? undefined : (e) => e.preventDefault()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-slate-400 transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-cyan-400/60 hover:text-white">
              <Icon className="h-4 w-4" />
            </a>
          ))}
        </div>
      </div>
      {cols.map((c) => (
        <div key={c.title} className="lg:col-span-3">
          <div className="eyebrow">{c.title}</div>
          <ul className="mt-5 space-y-3">
            {c.links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-slate-300 transition-colors hover:text-cyan-400">{l.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="lg:col-span-1" />
    </div>
    <div className="border-t border-line">
      <div className="wrap flex flex-col gap-4 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <span>© {new Date().getFullYear()} {SITE.name}. {SITE.location}.</span>
        <div className="flex gap-6">
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-slate-300">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-slate-300">Terms of Use</Link>
        </div>
      </div>
    </div>
  </footer>
);
